import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import service from '../service';

// Narrow query shape down or expand as needed
export type GetRestaurantsQuery = {
  name?: string;
  guid?: string;
  managementGroupGuid?: string;
};

export type ToastRestaurant = {
  guid: string;
  general: {
    name: string;
    locationName: string;
    locationCode: string | null;
    description: string;
    timeZone: string;
    closeoutHour: number;
    managementGroupGuid: string;
    currencyCode: string;
    firstBusinessDate: number;
    archived: boolean;
  };
  urls: {
    website: string;
    facebook: string;
    twitter: string;
    orderOnline: string;
    purchaseGiftCard: string;
    checkGiftCard: string;
  };
  location: {
    address1: string;
    address2: string;
    city: string;
    stateCode: string;
    administrativeArea: string;
    zipCode: string;
    country: string;
    phone: string;
    phoneCountryCode: string | null;
    latitude: number;
    longitude: number;
  };
  schedules: {
    daySchedules: Record<
      string,
      {
        scheduleName: string;
        services: {
          name: string;
          hours: {
            startTime: string;
            endTime: string;
          };
          overnight: boolean;
        }[];
        openTime: string;
        closeTime: string;
      }
    >;
    weekSchedule: {
      monday: string;
      tuesday: string;
      wednesday: string;
      thursday: string;
      friday: string;
      saturday: string;
      sunday: string;
    };
  };
  delivery: {
    enabled: boolean;
    minimum: number | null;
    area: string | null;
  };
  onlineOrdering: {
    enabled: boolean;
    scheduling: boolean;
    specialRequests: boolean;
    specialRequestsMessage: string;
    paymentOptions: {
      delivery: {
        cash: boolean;
        ccSameDay: boolean;
        ccFuture: boolean;
      };
      takeout: {
        cash: boolean;
        ccSameDay: boolean;
        ccFuture: boolean;
        ccInStore: boolean;
      };
      ccTip: boolean;
    };
  };
  prepTimes: {
    deliveryPrepTime: number;
    deliveryTimeAfterOpen: number;
    deliveryTimeBeforeClose: number;
    takeoutPrepTime: number;
    takeoutTimeAfterOpen: number;
    takeoutTimeBeforeClose: number;
    takeoutThrottlingTime: number;
    deliveryThrottlingTime: number;
  };
  // Extra fields allowed
  [k: string]: any;
};

export type RestaurantsResponse = {
  restaurants?: ToastRestaurant[];
  [k: string]: any;
};

type Params = {
  query?: GetRestaurantsQuery;
  enabled?: boolean;
};

export function useToastRestaurants<
  TData = ToastRestaurant[],
  TError = unknown,
>(
  { query, enabled = true }: Params,
  options?: Omit<
    UseQueryOptions<ToastRestaurant[], TError, TData>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const q = useQuery<ToastRestaurant[], TError, TData>({
    queryKey: [
      'toastRestaurants',
      query?.name ?? null,
      query?.guid ?? null,
      query?.managementGroupGuid ?? null,
    ],
    enabled: Boolean(enabled),
    queryFn: async () => {
      // Adjust this to match your actual service shape:
      // e.g., if it's already ToastRestaurant[], just return that.
      const res = (await service.getRestaurants(query ?? {})) as
        | RestaurantsResponse
        | ToastRestaurant[]
        | undefined;

      const restaurants = Array.isArray(res) ? res : (res?.partners ?? []);

      return restaurants as any;
    },
    ...(options as any),
  });

  return q;
}
